import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

/**
 * AgentesConectados — IAs propias enchufadas al gateway.
 * Misma persona = mismo s.label (case-insensitive): Salomón Claude + Salomón Clave
 * son UN agente con dos canales. Desconectar sigue siendo por sesión.
 * Agentes.jsx pinta el equipo; este archivo agrupa, carga y revoca.
 */
const GATEWAY = axios.create({
  baseURL: import.meta.env.VITE_AGENT_GATEWAY_URL || 'https://api-beta-v2.flowexpay.com/agent-gateway',
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
});
GATEWAY.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CANAL_ORDEN = ['Claude', 'Cursor', 'ChatGPT', 'Clave'];

export function canalDe(s) {
  const c = String(s?.client || '').toLowerCase();
  if (c === 'claude') return 'Claude';
  if (c === 'cursor') return 'Cursor';
  if (c === 'chatgpt') return 'ChatGPT';
  if (c === 'oraculo' || c === 'oráculo') return 'Oráculo';
  return 'Clave';
}

export function permisosDe(scopes = []) {
  return [
    { label: 'Consultar tu información', ok: scopes.includes('agent:read') },
    { label: 'Simular operaciones', ok: scopes.includes('agent:preview') },
    { label: 'Mover tu dinero', ok: false },
    { label: 'Retirar fondos', ok: false },
  ];
}

export function groupSessionsByLabel(sesiones = []) {
  const map = new Map();
  for (const s of sesiones) {
    if (s.status === 'revoked') continue;
    const raw = String(s.label || s.clientLabel || 'tu IA').trim() || 'tu IA';
    const key = raw.toLowerCase();
    if (!map.has(key)) {
      map.set(key, {
        key: `ia:${key}`,
        label: raw,
        sessions: [],
        canales: [],
        lastSeenAt: null,
        scopes: [],
      });
    }
    const g = map.get(key);
    g.sessions.push(s);
    const canal = canalDe(s);
    if (!g.canales.includes(canal)) g.canales.push(canal);
    if (s.lastSeenAt && (!g.lastSeenAt || String(s.lastSeenAt) > String(g.lastSeenAt))) {
      g.lastSeenAt = s.lastSeenAt;
      g.label = raw;
    }
    const have = new Set(g.scopes);
    for (const sc of (s.scopes || [])) have.add(sc);
    g.scopes = [...have];
  }
  for (const g of map.values()) {
    g.canales.sort((a, b) => {
      const ia = CANAL_ORDEN.indexOf(a);
      const ib = CANAL_ORDEN.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }
  return [...map.values()];
}

// Llaves del usuario (para saber si la IA de una ficha esta en pausa) y reactivarla.
export async function fetchLlaves() {
  const r = await GATEWAY.get('/agent/v1/me/keys');
  return r.data.keys || [];
}
export async function reactivarLlave(id) {
  await GATEWAY.post(`/agent/v1/me/keys/${id}/resume`);
}

export async function fetchActividad(agentIds = []) {
  const ids = new Set((agentIds || []).filter(Boolean));
  const r = await GATEWAY.get('/agent/v1/me/activity', { params: { limit: 50 } });
  return (r.data.entries || []).filter((e) => ids.has(e.agentId));
}

function labelFuente(e) {
  const raw = e?.fuenteExterna?.label;
  if (raw == null) return '';
  return String(raw).trim();
}

function porFechaDesc(a, b) {
  return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
}

/**
 * Diario ia-externa → UNA persona (el grupo). Label vs grupo.label, sin mayúsculas.
 * Sin label: solo si hay una sola IA conectada; si hay varias, todo el diario con una nota.
 */
export function filtrarDiarioPorGrupo(eventos = [], grupo, gruposCount = 1) {
  const list = Array.isArray(eventos) ? eventos : [];
  const key = String(grupo?.label || '').trim().toLowerCase();
  const conLabel = [];
  const sinLabel = [];
  for (const e of list) {
    if (labelFuente(e)) conLabel.push(e);
    else sinLabel.push(e);
  }
  const matching = key
    ? conLabel.filter((e) => labelFuente(e).toLowerCase() === key)
    : [];

  if (conLabel.length === 0) {
    if (gruposCount <= 1) return { eventos: [...list].sort(porFechaDesc), nota: null };
    return {
      eventos: [...list].sort(porFechaDesc),
      nota: 'No supe de quién es cada nota. Te muestro todas.',
    };
  }
  if (gruposCount <= 1) {
    return { eventos: [...matching, ...sinLabel].sort(porFechaDesc), nota: null };
  }
  return { eventos: matching.sort(porFechaDesc), nota: null };
}

export function useSesionesConectadas() {
  const [sesiones, setSesiones] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(() => {
    GATEWAY.get('/agent/v1/me/sessions')
      .then((r) => setSesiones((r.data.sessions || []).filter((s) => s.status !== 'revoked')))
      .catch(() => setSesiones([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const revocar = async (id) => {
    if (!window.confirm('¿Desconectar este canal? Dejará de poder llamar a FlowexPay con esa sesión.')) return false;
    try {
      await GATEWAY.post(`/agent/v1/me/sessions/${id}/revoke`);
      cargar();
      return true;
    } catch {
      return false;
    }
  };

  const grupos = useMemo(() => groupSessionsByLabel(sesiones), [sesiones]);
  return { sesiones, grupos, loading, cargar, revocar };
}

/** Lista agrupada compacta (no montada en Agentes.jsx; el equipo vive ahí). */
export default function AgentesConectados() {
  const { grupos, revocar } = useSesionesConectadas();
  if (!grupos.length) return null;
  return (
    <div className="mt-4 space-y-2">
      {grupos.map((g) => (
        <div key={g.key} className="rounded-xl border border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{g.label}</span>
            {g.canales.map((c) => (
              <span key={c} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{c}</span>
            ))}
            <span className="text-xs px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">Activo</span>
          </div>
          <div className="mt-2 space-y-1">
            {g.sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 text-xs text-gray-500">
                <span>{canalDe(s)}</span>
                <button type="button" onClick={() => revocar(s.id)} className="text-red-600 font-medium">Desconectar</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
